import { useState, type FC } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Translate, useTranslation } from "@privacy/translations";
import { Box } from "@mui/material";
import {
  deleteInformationTypeSystemConnectionMutationOptions,
  createInformationTypeSystemConnectionMutationOptions,
  type ConnectedSystem,
  type ConnectionType,
} from "../../api/informationTypes";
import { systemSelectOptionsQueryOptions } from "../../api/systems";
import { SystemIcon2 } from "../../shared/icons";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { ConfirmDeleteDialog } from "../../shared/ConfirmDeleteDialog";
import { Drawer } from "../../shared/Drawer";
import { ConnectionCriticality } from "../../classification/ConnectionCriticality";
import { usePermission } from "../../shared/permissions";

type RemoveDialogState =
  { isOpen: true; connection: ConnectedSystem } | { isOpen: false; connection?: ConnectedSystem };

const SYSTEM_ROW_ICON = <SystemIcon2 size="sm" />;

type InformationTypeConnectionsProps = {
  informationTypeId: string;
  connectionType: ConnectionType;
  connections: Array<ConnectedSystem>;
  canEdit: boolean;
};

export const InformationTypeConnections: FC<InformationTypeConnectionsProps> = ({
  informationTypeId,
  connectionType,
  connections,
  canEdit,
}) => {
  const { t } = useTranslation();
  const { navigate, paths } = usePrivacyNavigate();
  const { hasPermission } = usePermission();
  const createConnection = useMutation(createInformationTypeSystemConnectionMutationOptions());
  const deleteConnection = useMutation(deleteInformationTypeSystemConnectionMutationOptions());

  const [enableOptionsQuery, setEnableOptionsQuery] = useState(false);
  const systemOptionsQuery = useQuery({ ...systemSelectOptionsQueryOptions(), enabled: enableOptionsQuery });

  const [removeDialog, setRemoveDialog] = useState<RemoveDialogState>({ isOpen: false });

  return (
    <>
      <Drawer.Connections>
        {[...connections]
          .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
          .map(connection => (
            <Drawer.Connections.Item
              key={connection.connectionId}
              icon={SYSTEM_ROW_ICON}
              name={connection.name}
              onNavigate={() => navigate(paths.systemsInline(connection.systemId))}
              disableNavigation={!hasPermission("nova-forms-api:system:read")}
              endSlot={
                <>
                  {canEdit && (
                    <Drawer.Connections.RemoveButton
                      onRemove={() => setRemoveDialog({ isOpen: true, connection })}
                      ariaLabel={t("InformationType.Connection.Remove.Tooltip")}
                    />
                  )}
                  {connection.classification?.criticality && (
                    <ConnectionCriticality value={connection.classification.criticality.value} />
                  )}
                </>
              }
            />
          ))}
        {connections.length === 0 && !createConnection.isPending ? (
          <Drawer.Connections.EmptyState
            title={t(
              connectionType === "stored"
                ? "InformationType.Connection.StoredIn.EmptyState.Title"
                : "InformationType.Connection.UsedIn.EmptyState.Title"
            )}
            body={t(
              connectionType === "stored"
                ? "InformationType.Connection.StoredIn.EmptyState.Body"
                : "InformationType.Connection.UsedIn.EmptyState.Body"
            )}
          />
        ) : createConnection.isPending && !connections.some(c => c.systemId === createConnection.variables.systemId) ? (
          <Drawer.Connections.OptimisticItem
            icon={SYSTEM_ROW_ICON}
            name={createConnection.variables.systemName}
            onNavigate={() => navigate(paths.systemsInline(createConnection.variables.systemId))}
            disableNavigation={!hasPermission("nova-forms-api:system:read")}
          />
        ) : null}
        {canEdit && (
          <Drawer.Connections.Add
            options={systemOptionsQuery.data}
            excludeIds={[
              ...connections.map(c => c.systemId),
              ...(createConnection.isPending ? [createConnection.variables.systemId] : []),
            ]}
            onMouseEnter={() => setEnableOptionsQuery(true)}
            onFocus={() => setEnableOptionsQuery(true)}
            onAdd={option =>
              createConnection.mutateAsync({
                informationTypeId,
                systemId: option.id,
                systemName: option.name,
                connectionType,
              })
            }
            searchPlaceholder={t("InformationType.Connection.Picker.SearchPlaceholder")}
          />
        )}
      </Drawer.Connections>
      {removeDialog.isOpen && (
        <ConfirmDeleteDialog
          isOpen
          onClose={() => setRemoveDialog({ isOpen: false })}
          onDelete={() =>
            deleteConnection.mutateAsync({ informationTypeId, connectionId: removeDialog.connection.connectionId })
          }
          headerText={t("InformationType.Connection.Remove.ConfirmHeader")}
          message={
            <Translate
              translationKey="InformationType.Connection.Remove.ConfirmMessage"
              values={{
                name: (
                  <Box component="span" sx={{ fontWeight: "bold" }}>
                    {removeDialog.connection.name}
                  </Box>
                ),
              }}
            />
          }
          confirmButtonLabel={t("InformationType.Connection.Remove.ConfirmButton")}
        />
      )}
    </>
  );
};
